/* eslint-disable no-nested-ternary */
import { Box, Typography } from "@mui/material";
import DOMPurify from "dompurify";
import { FC, useState } from "react";

import ActiveFormMUI from "web/Layout/SubscriptionReceivers/SubscriptionReceiverForm/ActiveForm/activeFormMUI";
import ReceiverEmailFormMUI from "web/Layout/SubscriptionReceivers/SubscriptionReceiverForm/ReceiverEmailForm/receiverEmailFormMUI";
import SimpleFormMUI from "web/Layout/SubscriptionReceivers/SubscriptionReceiverForm/SimpleForm/simpleFormMUI";
import __ from "web/Layout/Translations";

import Dialog from "web/Components/mui/Dialog";

import {
  FormType,
  FormView,
  ISubscriptionFormData,
  ISubscriptionFormMode,
  ISubscriptionReceiverFormField,
  ISubscriptionReceiverFormValues,
  SubscriptionActiveFormData,
  SubscriptionFormReceiver,
} from "web/types/Subscription";
import { Nullable } from "web/types/Utils";

import SubscriptionCancelMUI from "./subscriptionCancelMUI";

interface ISubscriptionReceiverEditMUI {
  open: boolean;
  onClose: () => void;
  isMobile: boolean;
  groupName: string;
  subscriptionName: string;
  description?: string;
  formData: ISubscriptionFormData;
  configuration: SubscriptionActiveFormData[];
  mode: ISubscriptionFormMode;
  formType: FormType;
  receiverEmail: Nullable<string>;
  fields: ISubscriptionReceiverFormField[];
  nextSendAfter?: string;
  submitHandler: (values: ISubscriptionReceiverFormValues) => void;
  receiverType: SubscriptionFormReceiver;
  submitted: boolean;
  viewType: FormView;
}

const SubscriptionReceiverFormDialogEdit: FC<ISubscriptionReceiverEditMUI> = ({
  open,
  onClose,
  isMobile,
  groupName,
  subscriptionName,
  description,
  formData,
  configuration,
  mode,
  formType,
  receiverEmail,
  fields,
  nextSendAfter,
  submitHandler,
  receiverType,
  submitted,
  viewType,
}) => {
  const formWidth =
    formType === FormType.ACTIVE_FORM && !mode.externalForm ? "784px" : "612px";

  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [formDirty, setFormDirty] = useState(false);

  // Close attempt handler
  const handleAttemptClose = () => {
    if (formDirty) {
      setShowConfirmClose(true);
    } else {
      onClose();
    }
  };

  const handleCancelClose = () => {
    setShowConfirmClose(false);
  };

  const handleConfirmClose = () => {
    setShowConfirmClose(false);
    onClose();
  };

  const isEmployee = receiverType === SubscriptionFormReceiver.EMPLOYEE;

  // Active form
  if (formType === FormType.ACTIVE_FORM) {
    return (
      <>
        <Dialog
          sx={{
            "& .MuiDialog-paper": {
              maxWidth: isMobile ? "100%" : formWidth,
            },
            ...(!isMobile && {
              ".MuiDialogContent-root, & .MuiDialogTitle-root": {
                p: 0,
              },
              ".MuiDialogTitle-root": {
                pb: 3,
              },
            }),
            ...(isMobile &&
              !mode.externalForm && {
                ".MuiDialogContent-root": {
                  backgroundColor: "#FAFAFA",
                  padding: "0 !important",
                },
                ".MuiTypography-root": {
                  zIndex: 100,
                },
                ".MuiButtonBase-root": {
                  zIndex: 200,
                },
              }),
          }}
          fullScreen={isMobile}
          open={open}
          onClose={handleAttemptClose}
          title={submitted ? __("Edytuj dane") : __("Wypełnij formularz")}
        >
          <Box
            sx={{
              ...(isMobile &&
                !mode.externalForm && {
                  pt: 2,
                  px: 2,
                }),
            }}
          >
            <Typography variant="subtitle1" mb={1}>
              {groupName}
            </Typography>
            <Typography variant="h6" mb={2}>
              {subscriptionName}
            </Typography>
            {description && isEmployee && (
              <Typography
                variant="body2"
                mb={3}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(description),
                }}
              />
            )}
            <Typography variant="body2" mb={mode.externalForm ? 0 : 3}>
              {isEmployee
                ? submitted
                  ? __("Edytuj swoje dane")
                  : __("Uzupełnij swoje dane")
                : submitted
                ? __("Edytuj dane osób, dla których wybierasz abonament")
                : __("Uzupełnij dane osób, dla których wybierasz abonament")}
            </Typography>
            {mode.externalForm ? (
              <Typography variant="body2" pb={1}>
                {__("Na podany e-mail zostanie wysłany formularz")}
              </Typography>
            ) : null}
          </Box>
          <>
            {mode.externalForm ? (
              <ReceiverEmailFormMUI
                formData={formData}
                receiverEmail={receiverEmail}
                submitHandler={submitHandler}
                resend={mode.resend}
                nextSendAfter={nextSendAfter}
                onDirtyChange={setFormDirty}
                closeHandler={handleAttemptClose}
                isMobile={isMobile}
              />
            ) : (
              <ActiveFormMUI
                configuration={configuration}
                fields={fields}
                formData={formData}
                nameFieldsDisabled={mode.nameFieldsDisabled}
                submitHandler={submitHandler}
                onDirtyChange={setFormDirty}
                closeHandler={handleAttemptClose}
                isMobile={isMobile}
                excludeNameFieldsFromDependencies={isEmployee}
              />
            )}
          </>
        </Dialog>
        <SubscriptionCancelMUI
          handleCancelClose={handleCancelClose}
          handleConfirmClose={handleConfirmClose}
          isMobile={isMobile}
          showConfirmClose={showConfirmClose}
        />
      </>
    );
  }

  // Simple form
  return (
    <>
      <Dialog
        sx={{
          "& .MuiDialog-paper": {
            maxWidth: isMobile ? "100%" : formWidth,
          },
          ...(!isMobile && {
            ".MuiDialogContent-root, & .MuiDialogTitle-root": {
              p: 0,
            },
            ".MuiDialogTitle-root": {
              pb: 3,
            },
          }),
        }}
        fullScreen={isMobile}
        open={open}
        onClose={handleAttemptClose}
        title={submitted ? __("Edytuj dane") : __("Wypełnij formularz")}
      >
        <Typography variant="subtitle1" mb={1}>
          {groupName}
        </Typography>
        <Typography variant="h6" mb={2}>
          {subscriptionName}
        </Typography>
        <Typography variant="body2" mb={mode.externalForm ? 0 : 3}>
          {isEmployee
            ? submitted
              ? __("Edytuj swoje dane")
              : __("Uzupełnij swoje dane")
            : submitted
            ? __("Edytuj dane osób, dla których wybierasz abonament")
            : __("Uzupełnij dane osób, dla których wybierasz abonament")}
        </Typography>
        <SimpleFormMUI
          fields={fields}
          nameFieldsDisabled={mode.nameFieldsDisabled}
          formData={formData}
          submitHandler={submitHandler}
          onDirtyChange={setFormDirty}
          closeHandler={handleAttemptClose}
          isMobile={isMobile}
          viewType={viewType}
        />
      </Dialog>
      <SubscriptionCancelMUI
        handleCancelClose={handleCancelClose}
        handleConfirmClose={handleConfirmClose}
        isMobile={isMobile}
        showConfirmClose={showConfirmClose}
      />
    </>
  );
};

export default SubscriptionReceiverFormDialogEdit;
